import { EMPTY_GENERATED_FILES_SELECTION } from "@frontend-shared/constants";
import type {
  IGeneratedFile,
  TGeneratedFilesSelection,
} from "@frontend-shared/types";
import {
  resolveGeneratedFilesPickerState,
  toggleGeneratedFileSelection,
} from "@frontend-shared/utils";
import { useCallback, useMemo, useState } from "react";

import type {
  IUseChatGeneratedFilesSelectionProps,
  IUseChatGeneratedFilesSelectionResult,
} from "./types";

// NOTE: NSAI-5312 — the dialog opens with every listed file ticked; the
// shared resolveGeneratedFilesPickerState derives what it shows (and
// resolvePickedGeneratedFiles why a refetch cannot smuggle a vanished file into
// the download).
export const useChatGeneratedFilesSelection = ({
  files,
}: IUseChatGeneratedFilesSelectionProps): IUseChatGeneratedFilesSelectionResult => {
  const [pickedFiles, setPickedFiles] =
    useState<TGeneratedFilesSelection | null>(null);

  const pickerState = useMemo(
    () => resolveGeneratedFilesPickerState({ files, pickedFiles }),
    [files, pickedFiles],
  );
  const { selection } = pickerState;

  // NOTE: Toggles against what is ticked on screen, so the first untick of
  // the "everything" state starts from exactly the listed files.
  const toggleFile = useCallback(
    (file: IGeneratedFile): void => {
      setPickedFiles(
        toggleGeneratedFileSelection({ selectedFiles: selection, file }),
      );
    },
    [selection],
  );

  const setAllSelected = useCallback((isSelected: boolean): void => {
    setPickedFiles(isSelected ? null : EMPTY_GENERATED_FILES_SELECTION);
  }, []);

  return useMemo(
    () => ({ ...pickerState, toggleFile, setAllSelected }),
    [pickerState, setAllSelected, toggleFile],
  );
};
